import React, { useRef, useEffect, useState, useContext } from "react";
import CustomDivider from "../SurveyManagement/CustomDivider";
import {
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import "../SurveyManagement/Table.css";
import { toPng } from "html-to-image";
import SignaturePage from "./SignaturePage";
import ViewImage from "./ViewImage";
import { useTranslation } from "react-i18next";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { SnackbarContext } from "context/snackBarContext";
import WoodSupplierTaskService from "../../../services/WoodSupplierTaskService";
import CommonService from "services/CommonService";
import fontkit from "@pdf-lib/fontkit";
import { convertArabic } from "arabic-persian-reshaper/ArabicShaper";

const ViewSurvey = ({
  surveyData, // This is the selected row data from survey table
  onBack,
  onShowSignature,
  onRefreshData,
  isApprover,
  selectedCropStage,
  selectedCropStageId,
  year,
  read_only_user,
}) => {
  const signatureRef = useRef(null);
  const [viewSurveyData, setViewSurveyData] = useState([]); // Detailed survey data from API
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("");
  const [downloadPdfModalOpen, setDownloadPdfModalOpen] = useState(false);
  const [signatureAlertOpen, setSignatureAlertOpen] = useState(false);
  const [moduleNames, setModuleNames] = useState([]);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [signaturePageLoaded, setSignaturePageLoaded] = useState(false);
  const [countries, setCountries] = useState([]);
  const [cropStageName, setCropStageName]=useState("");
  const [pdfLanguages, setPdfLanguages] = useState(["English"]);
  const [pdfLanguage, setPdfLanguage] = useState("");
  const [labelTranslations, setLabelTranslations] = useState(
    JSON.parse(sessionStorage.getItem("SRLabelTranslations"))
  );
  const [pdfLabels, setPdfLabels] = useState({});
  const [signatureData, setSignatureData] = useState(null);
  const { t } = useTranslation();
  const { setOpenAlert, setSeverity, setMessage } = useContext(SnackbarContext);
  const status = surveyData?.wsSurveyStatus?.toLowerCase();
  const surveyHeaderId = surveyData?.wsHeaderId || null;
  // const cropStageTitle = selectedCropStage;
  const user = JSON.parse(sessionStorage.getItem("BAT_USER"));
  const countryCode = user?.countryCode;
  const cropYear = year;
  const cropStageId = selectedCropStageId;

  // Update the fetchSurveyDetails function
  const fetchSurveyDetails = async () => {
    try {
      let headers = {
        apiCountryCode: countryCode,
      };

      const response = await WoodSupplierTaskService.getViewSurvey(
        surveyHeaderId,
        cropYear,
        headers
      );
      console.log("response", response);
      setCropStageName(response?.data?.response?.surveyDetailDto?.cropStageName)

      if (response?.data?.response?.surveyDetailDto?.modules) {
        // Extract modules from the new response structure
        const modules = response.data.response.surveyDetailDto.modules;
        setViewSurveyData(modules);
        let signatureData = response?.data?.response?.surveyHeaderDto;
        setSignatureData(signatureData);

        // Get module names from the new structure
        const moduleNamesArray = modules.map((module) => module.moduleName);
        setModuleNames(moduleNamesArray);
        setActiveTab(moduleNamesArray[0] || "");
        setIsLoaded(true);
      }
    } catch (err) {
      console.error("Error fetching survey details:", err);

      if (err?.response?.status === 404) {
        setSeverity("error");
        setMessage("No record found");
        setOpenAlert(true);
      } else {
        setSeverity("error");
        setMessage("An error occurred while fetching data");
        setOpenAlert(true);
      }
    }
  };

  const fetchCountries = () => {
    CommonService?.fetchCountries()
      .then((res) => {
        let countries = res?.data?.response;
        setCountries(countries);
      })
      .catch((ex) => {
        setCountries([]);
        console.log("error", ex);
      });
  };

  const fetchLabelTranslations = () => {
    const config = {
      headers: {
        apiCountryCode: countryCode,
      },
    };
    WoodSupplierTaskService.getLabelTranslations(config)
      .then((res) => {
        let translations = res?.data?.response;
        const labelsObject = {
          country: countryCode,
          translations: translations,
        };
        sessionStorage.setItem(
          "SRLabelTranslations",
          JSON.stringify(labelsObject)
        );
        setLabelTranslations(labelsObject);
      })
      .catch((ex) => {
        setLabelTranslations({});
        console.log("error", ex);
      });
  };

  useEffect(() => {
    if (countries) {
      const localLanguage = countries.find(
        (ele) => ele?.countryCode === countryCode
      )?.localLanguage;
      if (
        localLanguage &&
        localLanguage !== "English" &&
        !pdfLanguages.includes(localLanguage)
      ) {
        setPdfLanguages((prev) => [...prev, localLanguage]);
      }
    }
  }, [countries]);

  useEffect(() => {
    if (!labelTranslations?.translations) return;

    const keys = [
      "signature_page",
      "wsfarmerName",
      "wsgrowerNumber",
      "wsotherPersonName",
      "wsotherPersonAge",
      "wsdateSurvey",
      "wsphoto",
      "wsgeoCoordinate",
      "wscomments",
      "wssignature",
      "wswmName",
      "wswmSignature",
      "question_code",
      "question_text",
      "answer",
    ];

    const labels = keys.reduce((acc, key) => {
      const item = labelTranslations?.translations?.find((obj) => obj.ewsKey === key);
        if (pdfLanguage === "English" || !pdfLanguage) {
          acc[key] = item?.ewsEnglishText;
        } else if (pdfLanguage === "Urdu" && item?.ewsLocalText) {
          acc[key] = convertArabic(item?.ewsLocalText);
        } else if (pdfLanguage === "Bangla" && item?.ewsLocalText) {
          acc[key] = convertBangla(item?.ewsLocalText);
        } else {
          acc[key] = item?.ewsLocalText;
        }
        acc[key] = acc[key] || "";
      return acc;
    }, {});

    setPdfLabels(labels);
  }, [pdfLanguage, labelTranslations]);

  useEffect(() => {
    if (!labelTranslations || labelTranslations?.country !== countryCode) {
      fetchLabelTranslations();
    }
  }, [countryCode]);

  useEffect(() => {
    fetchSurveyDetails();
  }, [surveyHeaderId]);

  // Ensure fonts and images are fully loaded
  useEffect(() => {
    if (!isLoaded) {
      document.fonts.ready.then(() => setIsLoaded(true));
    }
    fetchCountries();
  }, []);

  const handleDownloadPdfClick = () => {
    setDownloadPdfModalOpen(true);
  };

  const handleDownloadPdfModalClose = () => {
    setDownloadPdfModalOpen(false);
    pdfLanguage && setPdfLanguage("");
  };

  const handleSignatureClick = (signatureData) => {
    if (status === "completed") {
      onShowSignature(signatureData);
    } else {
      setSignatureAlertOpen(true);
    }
  };

  const handleSignatureAlertClose = () => {
    setSignatureAlertOpen(false);
  };

  const toBase64 = (str) => {
    return btoa(
      new TextEncoder()
        .encode(str)
        .reduce((data, byte) => data + String.fromCharCode(byte), "")
    );
  };

  const convertBangla = (text) => {
    if (!text) return text;

    const hasBangla = /[\u0980-\u09FF]/.test(text);

    if (!hasBangla) return text;

    return text.normalize("NFC");
  };

  const handleDownloadPDF = async () => {
    setDownloadPdfModalOpen(false);
    setDownloadLoading(true);

    if (!signatureRef.current?.element) return;

    try {
      // Create PDF document
      const pdfDoc = await PDFDocument.create();
      pdfDoc.registerFontkit(fontkit);
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const helveticaBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      let pdfFont;
      if (["Uzbek", "Croatian", "Portuguese", "Spanish", "Vietnamese"].includes(pdfLanguage)) {
        const fontBytes = await fetch("/assets/fonts/NotoSans-Regular.ttf")
          .then((res) => res.arrayBuffer());
        pdfFont = await pdfDoc.embedFont(fontBytes);
      } else if (pdfLanguage === "Urdu") {
        const urduFontBytes = await fetch("/assets/fonts/NotoNaskhArabic-Regular.ttf")
          .then((res) => res.arrayBuffer());
        // GPOS mark positioning makes fontkit throw (xCoordinate of null) on alef-madda/heh-yeh-above glyphs in this font
        pdfFont = await pdfDoc.embedFont(urduFontBytes, { features: { mark: false } });
      } else if (pdfLanguage === "Bangla") {
        const banglaFontBytes = await fetch(
          "/assets/fonts/NotoSansBengali.ttf"
        ).then((res) => res.arrayBuffer());
        pdfFont = await pdfDoc.embedFont(banglaFontBytes, {
          subset: true,
          unicode: true,
          features: {
            liga: true, // Enable ligatures
            kern: true, // Enable kerning
          },
        });
      } else {
        pdfFont = helveticaFont;
      }

      let page = pdfDoc.addPage([841.89, 595.28]);
      const { width, height } = page.getSize();

      const imageDataUrl = await toPng(signatureRef?.current?.element, {
        cacheBust: true,
        backgroundColor: "#ffffff",
        quality: 1,
        pixelRatio: 2,
      });

      const pngImageBytes = await fetch(imageDataUrl).then((res) =>
        res.arrayBuffer()
      );
      const pngImage = await pdfDoc.embedPng(pngImageBytes);

      const margin = 50; // same margin used in the rest of the doc
      const pageWidth = width; // width = 595.28 (portrait A4)
      const maxWidth = pageWidth - margin * 2;

      // Step 1: Get original image dimensions
      const pngDims = pngImage.scale(1); // full size
      const aspectRatio = pngDims.height / pngDims.width;

      // Step 2: Scale width to maxWidth and compute proportional height
      const finalWidth = maxWidth;
      const finalHeight = finalWidth * aspectRatio;
      // Draw image at top of page with margin
      page.drawImage(pngImage, {
        x: margin,
        y: height - margin - finalHeight, // Position from top
        width: finalWidth,
        height: finalHeight,
      });

      // Start content after the image
      let yOffset = height - margin - finalHeight - 50; // 50px spacing below the image
      // Adding 50px spacing above the image

      // Header styling
      const drawHeader = (text = "", y) => {
        // Main module header - teal color with partial width
        page.drawRectangle({
          x: 50,
          y: y - 30,
          width:width-100,
          height: 40,
          color: rgb(0.13, 0.45, 0.6), // Teal color
        });
        page.drawText(text ?? "", {
          x: 60,
          y: y - 20,
          size: 16,
          font: (pdfLanguage === "English" || !pdfLanguage)
          ? helveticaBold
          : pdfFont,
          color: rgb(1, 1, 1),
        });
      };

      const drawSubmoduleHeader = (text = "", y) => {
          // Submodule header - dark blue with full width
          page.drawRectangle({
            x: 50,
            y: y - 25,
            width: width - 100,
            height: 35,
            color: rgb(0.06, 0.32, 0.54), // Dark blue
          });
          page.drawText(text ?? "", {
            x: 60,
            y: y - 12,
            size: 14,
            font: (pdfLanguage === "English" || !pdfLanguage)
            ? helveticaBold
            : pdfFont,
            color: rgb(1, 1, 1),
          });
      };

      const drawTableHeader = (y) => {
        // Question headers background - light gray
        page.drawRectangle({
          x: 50,
          y: y,
          width: width - 100,
          height: 30,
          color: rgb(0.95, 0.95, 0.95),
        });

        // Headers with white color for better visibility
        const headerStyle = {
          size: 12,
          color: rgb(0, 0, 0),
          font: pdfFont,
        };

        const questionCodeText = pdfLabels?.question_code ?? "";
        const questionTextText = pdfLabels?.question_text ?? "";
        const answerText = pdfLabels?.answer ?? "";

        // Question Code header
        page.drawText(questionCodeText, {
          x: 60,
          y: y + 10, // Adjusted Y position
          ...headerStyle,
        });

        // Question Text header
        page.drawText(questionTextText, {
          x: 190,
          y: y + 10, // Adjusted Y position
          ...headerStyle,
        });

        // Answer header
        page.drawText(answerText, {
          x: 590,
          y: y + 10, // Adjusted Y position
          ...headerStyle,
        });

        // Blue line below headers
        page.drawRectangle({
          x: 50,
          y: y,
          width: width - 100,
          height: 2,
          color: rgb(0.06, 0.32, 0.54), // Dark blue
        });

        return y - 35; // Return new Y position
      };

      // Modified function to calculate text line wrapping and return the lines
      const calculateTextLines = (rawText, maxWidth, font, size) => {
        // Text sanitization
        const text = rawText
          ? rawText
            .replace(/[\u2010-\u2015\u2212]/g, "-")
            .replace(/[\u2018\u2019]/g, "'")
            .replace(/[\u201C\u201D]/g, '"')
            .replace(/\u00A0/g, " ")
            .replace(/[\r\n\t\f\v]/g, " ")
            .replace(/\s+/g, " ")
            .trim()
          : "";

        const words = text.split(" ");
        const lines = [];
        let currentLine = "";

        for (const word of words) {
          // Skip empty words that might result from multiple spaces
          if (!word) continue;

          const lineTest = currentLine
            ? `${currentLine} ${word}`
            : word;
          const lineWidth = font.widthOfTextAtSize(lineTest, size);

          if (lineWidth <= maxWidth) {
            currentLine = lineTest;
          } else {
            lines.push(currentLine);
            currentLine = word;
          }
        }

        if (currentLine) {
          lines.push(currentLine);
        }

        return lines.length ? lines : [""];
      };

      const drawPreparedText = (
        lines1,
        lines2,
        x1,
        x2,
        y,
        size,
        font,
        color,
        lineHeight
      ) => {
        const linesLength = Math.max(lines1.length, lines2.length);
        let currentY = y;
        let i = 0;

        while (i < linesLength) {
          // Check if we need a new page
          if (currentY < 50) {
            // Create new page
            page = pdfDoc.addPage([841.89, 595.28]);
            // Start at a higher position on the new page to reduce gap
            currentY = height - 50;
          }

          // Draw text from first column
          if (i < lines1.length && lines1[i]) {
            try {
              page.drawText(lines1[i], {
                x: x1,
                y: currentY,
                size,
                font,
                color,
              });
            } catch (err) {
              console.warn(`Could not draw text line`, err);
              // Draw fallback text for this line
              page.drawText("[Text contains unsupported characters]", {
                x: x1,
                y: currentY,
                size,
                font,
                color,
              });
            }
          }

          // Draw text from second column
          if (i < lines2.length && lines2[i]) {
            try {
              page.drawText(lines2[i], {
                x: x2,
                y: currentY,
                size,
                font,
                color,
              });
            } catch (err) {
              console.warn(`Could not draw text line`, err);
              // Draw fallback text for this line
              page.drawText("[Text contains unsupported characters]", {
                x: x2,
                y: currentY,
                size,
                font,
                color,
              });
            }
          }

          // Move to next line
          currentY -= lineHeight;
          i++;
        }

        // Return the final y position after all lines are drawn
        return currentY;
      };

      // Add this recursive function to handle child responses in PDF generation
      const processQuestionsRecursively = (questions) => {
        const allQuestions = [];

        questions.forEach((item) => {
          // Add the main question
          allQuestions.push(item);

          // If this question has child responses, process them recursively
          if (item.childResponses && item.childResponses.length > 0) {
            const childQuestions = processQuestionsRecursively(item.childResponses);
            allQuestions.push(...childQuestions);
          }
        });

        return allQuestions.sort(
          (a, b) =>
          (a?.questionnaire?.sequence ?? 0) - (b?.questionnaire?.sequence ?? 0)
        );
      };

      const drawQuestionRow = async (detail, y) => {
        // Sanitize and prepare question and answer text
        let questionText =
          (pdfLanguage && pdfLanguage !== "English"
            ? detail?.questionnaire?.questionTextLL
            : detail?.questionnaire?.questionTextEng) || "";
        let newResponseText = (pdfLanguage && pdfLanguage != "English") ?   detail?.responseText || detail?.responseStandard:  detail?.responseStandard
        let responseText = (pdfLanguage && pdfLanguage != "English") ? newResponseText :
        newResponseText ?
        newResponseText.replace(/[\r\n\t\f\v]/g, " ")
        .replace(/\s+/g, " ")
        .replace(/[^\x20-\x7E]/g, "")
        .trim():""

        if(pdfLanguage==="Urdu" && questionText ) questionText=convertArabic(questionText)
        if(pdfLanguage==="Bangla" && questionText ) questionText=convertBangla(questionText)

          if(pdfLanguage==="Urdu" && responseText ) responseText=convertArabic(responseText)
            if(pdfLanguage==="Bangla" && responseText ) responseText=convertBangla(responseText)

        const imagesHeight =
          Math.ceil(detail?.picUrlList?.length / 7 || 0) * 23;

        // Calculate lines for both question and answer
        const questionLines = calculateTextLines(
          questionText,
          pdfLanguage === "Bangla" ? 340 : 380, // Question text column width
          pdfFont,
          10
        );

        const responseLines = calculateTextLines(
          responseText,
          200, // Response text column width
          pdfFont,
          10
        );

        const lineHeight = 12; // line height for both columns
        const questionHeight = questionLines.length * lineHeight;
        const responseHeight = responseLines.length * lineHeight;

        // Total row height is the maximum of both columns
        const totalRowHeight = Math.max(
          questionHeight,
          responseHeight,
          imagesHeight
        );

        // Draw horizontal lines for the row
        const lineColor = rgb(0.8, 0.8, 0.8);

        // Top border
        page.drawLine({
          start: { x: 50, y: y + 60 },
          end: { x: width - 50, y: y + 60 },
          thickness: 1,
          color: lineColor,
        });

        // Bottom border
        page.drawLine({
          start: { x: 50, y: y + 40 - totalRowHeight },
          end: { x: width - 50, y: y + 40 - totalRowHeight },
          thickness: 1,
          color: lineColor,
        });

        // Question code (remains unchanged)
        page.drawText(detail?.questionnaire?.questionCode || "", {
          x: 60,
          y: y + 40,
          size: 10,
          font: pdfFont,
          color: rgb(0.2, 0.2, 0.2),
          maxWidth: 110,
        });

        if (detail?.picUrlList) {
          for (let index = 0; index < detail?.picUrlList.length; index++) {
            const url = detail?.picUrlList[index];
            const res = await WoodSupplierTaskService.getImageUrl(
              toBase64(url)
            );
            const imageUrl =
              "data:image/jpeg;base64," + res?.data?.response?.fileContent;

            if (imageUrl) {
              const imageBytes = await fetch(imageUrl).then((res) =>
                res.arrayBuffer()
              );
              let image;
              if (imageUrl.startsWith('data:image/jpeg') || imageUrl.startsWith('data:image/jpg')) {
                image = await pdfDoc.embedJpg(imageBytes);
              } else if (imageUrl.startsWith('data:image/png')) {
                image = await pdfDoc.embedPng(imageBytes);
              } else {
                // For other formats, try JPEG as fallback
                try {
                  image = await pdfDoc.embedJpg(imageBytes);
                } catch (error) {
                  console.error(`Failed to embed image at index ${index}:`, error);
                  continue; // Skip this image and continue with the next one
                }
              }

              const row = Math.floor(index / 7);
              const col = index % 7;

              page.drawImage(image, {
                x: 590 + col * 30,
                y: y + 30 - row * 30, // decrease Y to move "down"
                width: 20,
                height: 20,
              });
            }
          }
        }

        const currentY = drawPreparedText(
          questionLines,
          responseLines,
          190, // X position of question text
          590,
          y + 40, // Y position
          10, // font size
          pdfFont,
          rgb(0.2, 0.2, 0.2),
          lineHeight
        );

        // Bottom border
        currentY > totalRowHeight &&
          totalRowHeight !== imagesHeight &&
          page.drawLine({
            start: { x: 50, y: currentY },
            end: { x: width - 50, y: currentY },
            thickness: 1,
            color: lineColor,
          });

        // Return the new Y position
        return totalRowHeight > imagesHeight
          ? currentY - 70
          : y - 30 - totalRowHeight;
      };

      for (const module of viewSurveyData) {
        // Use viewSurveyData instead of surveyData
        if (yOffset < 300) {
          page = pdfDoc.addPage([841.89, 595.28]);
          yOffset = height - 50;
        }
        let moduleName = pdfLanguage && pdfLanguage!=="English"
          ? module?.moduleNameLL
          : module?.moduleName;
        if(pdfLanguage==="Urdu" && moduleName) moduleName=convertArabic(moduleName)
        if(pdfLanguage==="Bangla" && moduleName ) moduleName=convertBangla(moduleName)
        drawHeader(moduleName, yOffset);
        yOffset -= 60;

        for (const subModule of module?.subModules) {
          if (yOffset < 200) {
            page = pdfDoc.addPage([841.89, 595.28]);
            yOffset = height - 50;
          }
          let subModuleName = pdfLanguage && pdfLanguage!=="English"
            ? subModule?.subModuleNameLL
            : subModule?.subModuleName;
          if(pdfLanguage==="Urdu" && subModuleName) subModuleName=convertArabic(subModuleName)
          if(pdfLanguage==="Bangla" && subModuleName ) subModuleName=convertBangla(subModuleName)
          drawSubmoduleHeader(subModuleName, yOffset);
          yOffset -= 50;

          yOffset = drawTableHeader(yOffset);
          yOffset -= 40;

  const allQuestions = processQuestionsRecursively(subModule?.surveyDetails || []);

          for (const detail of allQuestions) {
            // Check if we need a new page
            if (yOffset < 50) {
              page = pdfDoc.addPage([841.89, 595.28]);
              yOffset = height - 100;
            }

            yOffset = await drawQuestionRow(detail, yOffset);
          }

          yOffset -= 30; // Add extra spacing between submodule sections
        }
      }

      // Get current date and time
      const now = new Date();
      const dateString = now.toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      });

      // Draw "Downloaded at {date time}" footer
      page.drawText(`${dateString}`, {
        x: 50,
        y: 30, // Near bottom margin
        size: 10,
        font: helveticaFont,
        color: rgb(0.5, 0.5, 0.5),
      });

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${cropStageName}_${
        surveyData?.woodSupplierName || "Wood Supplier"
      }_${status.charAt(0).toUpperCase() + status.slice(1)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF generation error:", err);
    } finally {
      setDownloadPdfModalOpen(false);
      pdfLanguage && setPdfLanguage("");
      setDownloadLoading(false);
    }
  };

  const renderTabContent = () => {
    // if (!viewSurveyData) return null;

    // Get the data for the active tab
    const currentModule = viewSurveyData.find(
      (module) => module.moduleName === activeTab
    );

    if (!currentModule) return null;

    const flattenBySequence = (questions) =>
      questions
        .flatMap((item) => [item, ...flattenBySequence(item.childResponses || [])])
        .sort(
          (a, b) =>
            (a?.questionnaire?.sequence ?? 0) - (b?.questionnaire?.sequence ?? 0)
        );

    const renderQuestionRows = (questions) => {
      const rows = [];

      flattenBySequence(questions).forEach((item, index) => {
        // Add main question row
        rows.push(
          <tr key={`${item.detailId}-${index}`}>
            <td>{item?.questionnaire?.questionCode}</td>
            <td>{item?.questionnaire?.questionTextEng}</td>
            <td>
              {item?.responseStandard || item.responseText
                ? item?.responseStandard || item.responseText
                : item.picUrlList?.length > 0
                ? item.picUrlList.map((url, i) => (
                    <ViewImage key={i} fileUrl={url} />
                  ))
                : " "}
            </td>
          </tr>
        );
      });

      return rows;
    };

    return (
      <Box>
        {currentModule.subModules.map((submodule) => (
          <Box key={submodule.subModuleName} sx={{ marginBottom: 2 }}>
            <Box
              sx={{
                backgroundColor: "#004F9F",
                color: "white",
                padding: "10px",
                fontWeight: "bold",
              }}
            >
              <Typography variant="subtitle1">
                {submodule.subModuleName}
              </Typography>
            </Box>

            {/* Table for questions */}
            <div className="table-container">
              <table className="view-survey-table">
                <thead>
                  <tr>
                    <th className="actions-header">
                      <div> {t("viewSurvey.questionCode")}</div>
                    </th>
                    <th className="actions-header">
                      <div>{t("viewSurvey.questionText")}</div>
                    </th>
                    <th className="actions-header">
                      <div> {t("viewSurvey.answer")}</div>
                    </th>
                  </tr>
                </thead>
                <tbody>{renderQuestionRows(submodule.surveyDetails)}</tbody>
              </table>
            </div>
          </Box>
        ))}
      </Box>
    );
  };

  const renderPDFLanguageModal = () => (
    <Modal open={downloadPdfModalOpen} onClose={handleDownloadPdfModalClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          borderRadius: "10px 10px 0px 0px",
          boxShadow: 24,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: "#0e3364",
            color: "white",
            padding: "15px 20px",
            textAlign: "center",
            borderRadius: "10px 10px 0px 0px",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontWeight: 500,
              fontStyle: "normal",
              fontSize: "20px",
              lineHeight: "20px",
              letterSpacing: "0.4px",
            }}
          >
            {t("viewSurvey.confirmation")}
          </Typography>
        </Box>

        <Box sx={{ padding: "20px" }}>
          {pdfLanguages.length === 1 ? (
            <Typography sx={{ mb: 3, textAlign: "center" }}>
              {t("viewSurvey.pdfDownloadConfirmationMsg")}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <FormControl
                sx={{
                  m: 1,
                  width: 220,
                  borderRadius: 1,
                  "& label": {
                    color: "black !important",
                    backgroundColor: "white",
                    padding: "0 4px",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px !important",
                    "& fieldset": {
                      borderColor: "gray", // default border
                    },
                    "&:hover fieldset": {
                      borderColor: "gray", // prevent blue on hover
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "gray", // prevent blue on focus
                      borderWidth: 1, // prevent thicker border
                    },
                  },
                }}
                size="small"
              >
                <InputLabel
                  id="demo-select-large-label"
                  sx={{ opacity: "60%" }}
                >
                  Select Language
                </InputLabel>
                <Select
                  labelId="demo-select-large-label"
                  id="demo-select-small"
                  value={pdfLanguage || "select"}
                  label="Select Language"
                  onChange={(e) => {
                    setPdfLanguage(e.target.value);
                  }}
                  sx={{
                    borderRadius: 0, // for the input
                  }}
                >
                  <MenuItem value="select" sx={{ display: "none" }}>
                    Select an option
                  </MenuItem>
                  {pdfLanguages?.map((item) => (
                    <MenuItem value={item}>{item}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 5,
              mt: 2.3,
            }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: "#6c757d",
                color: "white",
                "&:hover": { bgcolor: "#5a6268" },
                width: 100,
                p: "8px 14px",
              }}
              onClick={handleDownloadPdfModalClose}
            >
              {t("viewSurvey.no")}
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: "#0066b2",
                color: "white",
                "&:hover": { bgcolor: "#00559b" },
                width: 100,
                p: "8px 14px",
                "&.Mui-disabled": {
                  bgcolor: "lightgray",
                  color: "white",
                  opacity: 0.9,
                },
              }}
              disabled={!pdfLanguage && pdfLanguages.length > 1}
              onClick={handleDownloadPDF}
            >
              {t("viewSurvey.yes")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );

  const renderSignatureAlertModal = () => (
    <Modal open={signatureAlertOpen} onClose={handleSignatureAlertClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          borderRadius: "4px",
          boxShadow: 24,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: "#0e3364",
            color: "white",
            padding: "15px 20px",
            textAlign: "center",
          }}
        >
          <Typography variant="h6">{t("viewSurvey.info")}</Typography>
        </Box>

        {/* Content */}
        <Box sx={{ padding: "20px" }}>
          <Typography sx={{ mb: 3, textAlign: "center" }}>
            {t("viewSurvey.signatureInfoMsg")}
          </Typography>

          {/* OK Button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 3,
            }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: "#0066b2",
                color: "white",
                "&:hover": { bgcolor: "#00559b" },
                width: 100,
              }}
              onClick={handleSignatureAlertClose}
            >
              {t("viewSurvey.ok")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );

  return (
    <>
      <CustomDivider title={cropStageName} onBack={onBack} />

      {/* Use moduleNames state for tabs */}
      <Box sx={{ display: "flex", borderBottom: "1px solid #e0e0e0" }}>
        {moduleNames?.map((name) => (
          <Box
            key={name}
            sx={{
              position: "relative",
              padding: "10px 20px",
              cursor: "pointer",
            }}
            onClick={() => setActiveTab(name)}
          >
            <Typography
              sx={{
                color: activeTab === name ? "#ff8c00" : "inherit",
                fontWeight: activeTab === name ? "bold" : "normal",
              }}
            >
              {name}
            </Typography>
            {activeTab === name && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  height: "3px",
                  backgroundColor: "#ff8c00",
                }}
              />
            )}
          </Box>
        ))}
        <Box sx={{ marginLeft: "auto", display: "flex", alignItems: "center" }}>
          <Button
            sx={{
              textTransform: "none",
              color: "#0066cc",
              fontSize: "14px",
              padding: "10px 15px",
              textDecoration: "underline !important",
              "&:hover": {
                backgroundColor: "transparent",
                color: "#0066cc",
                boxShadow: "none",
              },
            }}
            onClick={() => {
              handleSignatureClick(signatureData);
            }}
          >
            {t("viewSurvey.signaturePage")}
          </Button>

          <Button
            sx={{
              textTransform: "none",
              color: "#0066cc",
              fontSize: "14px",
              padding: "10px 15px",
              textDecoration: "underline !important",
              "&:hover": {
                backgroundColor: "transparent",
                color: "#0066cc",
                boxShadow: "none",
              },
            }}
            onClick={handleDownloadPdfClick}
            disabled={
              read_only_user ||
              !isLoaded ||
              status !== "completed" ||
              downloadLoading ||
              !signaturePageLoaded
            }
          >
            {downloadLoading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <span>Downloading...</span>
              </Box>
            ) : (
              t("viewSurvey.downloadPDF")
            )}
          </Button>
        </Box>
      </Box>

      {/* Use formattedData for content */}
      <Box sx={{ mb: 10 }}>{renderTabContent()}</Box>

      {/* Hidden Signature Page */}
      {"completed" === status && (
        <Box
          sx={{
            position: "absolute",
            top: "-9999px",
            left: "-9999px",
          }}
        >
          <SignaturePage
            ref={signatureRef}
            data={signatureData} // Pass original survey data for signatures
            pdfLabels={pdfLabels}
            onSignatureLoad={setSignaturePageLoaded}
            onBack={onBack}
          />
        </Box>
      )}

      {/* Render the modals */}
      {renderPDFLanguageModal()}
      {renderSignatureAlertModal()}
    </>
  );
};

export default ViewSurvey;
